<template>
    <div class="vue-vehicle-overview vue-vehicle-overview-v2">
        <div class="header-wrapper" v-if="!iframe">
            <div class="container">
                <div class="row">
                    <div class="col-12">
                        <slot name="title"></slot>
                    </div>

                    <div class="row mt-3">
                        <div class="col-lg-10" v-if="!hideFilters">
                            <aut-filter-set
                                :filter-set="filterSet"
                                :is-open="filterSetOpen"
                                @filters-changed="handleFiltersChanged"
                                @toggle-filter-set="handleFilterSet"
                                :event-bus="eventBus"
                                :filter-amount="filteredVehicles.length"
                            ></aut-filter-set>
                        </div>
                        <div class="col-lg-2 d-flex justify-content-between justify-content-lg-end" v-if="!hideFilters">
                            <aut-view-toggle
                                :view-mode="viewMode"
                                @view-mode-changed="handleViewModeChanged"
                            ></aut-view-toggle>
                        </div>
                    </div>
                </div>

                <aut-slider-overview
                    v-if="hasVehicles && !loading && viewMode === 'slider'"
                    :location-id="locationId"
                    :vehicles="filteredVehicles"
                    :is-business="isBusiness"
                ></aut-slider-overview>
            </div>
        </div>

        <div class="container">
            <div class="row align-items-start">
                <div class="col-12 d-flex flex-column">
                    <div class="row d-lg-none" v-if="!iframe">
                        <div class="col-12">
                            <div class="filter-row">
                                <div class="row">
                                    <div class="col d-inline-flex">
                                        <button
                                            @click="toggleFilterSet"
                                            :aria-expanded="filterSetOpen ? 'true' : 'false'"
                                            aria-controls="vehicle-filter-set"
                                            class="filter-button btn-pill m-0 w-100"
                                        >
                                            {{ translations.filterText }}
                                        </button>

                                        <a
                                            v-if="locationId"
                                            href="#location-details"
                                            class="location-button btn-pill my-0 w-100"
                                        >
                                            Vestigingsinfo
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row h-100">
                        <div v-if="businessRentalPhrase && isBusiness" v-html="businessRentalPhrase"
                             class="business-rental-phrase col-12 mx-auto text-center"></div>

                        <aut-grid-overview
                            v-if="hasVehicles && !loading && viewMode === 'grid'"
                            :vehicles="filteredVehicles"
                            :location-id="locationId"
                            :cta="ctas?.cta"
                            :second-cta="ctas?.secondCta"
                            :is-business="isBusiness"
                            :iframe="iframe"
                        ></aut-grid-overview>

                        <template v-if="loading">
                            <div class="loading"></div>
                            <slot></slot>
                        </template>

                        <template v-if="!hasVehicles && !loading">
                            <p class="d-flex justify-content-center align-items-center mb-0 py-3">
                                {{ translations.noResultsText }}</p>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FilterSet from "./FilterSetV2";
import SortDropdown from "../SortDropdown";
import FilterBadge from "./FilterBadge";
import ViewToggle from "./ViewToggle.vue";
import translations from "../../mixins/translations";
import Vue from "vue";
import GridOverview from "./GridOverview.vue";
import SliderOverview from "./SliderOverview.vue";

export default {
    name: "Overview",

    components: {
        'aut-filter-set': FilterSet,
        'aut-sort-dropdown': SortDropdown,
        'aut-filter-badge': FilterBadge,
        'aut-view-toggle': ViewToggle,
        'aut-grid-overview': GridOverview,
        'aut-slider-overview': SliderOverview,
    },

    mixins: [translations],

    props: {
        locationId: {
            required: false,
        },
        vehicleType: {
            type: String
        },
        hideFilters: {
            type: Boolean,
            default: false
        },
        iframe: {
            type: Boolean,
            default: false
        },
        predefinedVehicleGroup: {
            type: String
        },
        businessRentalPhrase: {
            type: String,
            required: false,
            default: ''
        }
    },

    data() {
        return {
            vehicleGroup: null,
            vehicleGroupId: 102,
            vehicles: [],
            filterSet: {},
            ctas: {},
            sortOption: {sortBy: 'weight', order: 'asc'},
            selectedFilters: [],
            filterSetOpen: false,
            loading: true,
            eventBus: new Vue(),
            viewMode: 'grid',
            isBusiness: this.$route.query.business === "true",
        }
    },

    computed: {
        hasLocationId() {
            return !lodash.isNil(this.locationId);
        },

        hasVehicleGroup() {
            return !lodash.isNil(this.vehicleGroup);
        },

        sortedVehicles() {
            if (lodash.isEmpty(this.sortOption)) {
                return this.vehicles;
            }

            return lodash.orderBy(this.vehicles, [this.sortOption.sortBy], [this.sortOption.order]);
        },

        filteredVehicles() {
            if (lodash.isEmpty(this.selectedFilters)) {
                return this.sortedVehicles;
            }

            return this.sortedVehicles.filter(vehicle => {
                return this.selectedFilters.every(filterGroup => {
                    const { groupId, filters } = filterGroup;
                    const vehicleValues = vehicle.filterable_values[groupId];

                    if (!vehicleValues) return false;

                    return filters.some(filter => vehicleValues.includes(filter));
                });
            });
        },

        hasVehicles() {
            return !lodash.isEmpty(this.filteredVehicles);
        },

        vehiclesForAnalytics() {
            return lodash.map(this.filteredVehicles, (vehicle) => {
                let object = {
                    item_id: vehicle.id,
                    item_name: vehicle.title,
                    item_category: vehicle.vehicle_group.label,
                    price: vehicle.price_per_day,
                }
                if (!lodash.isNil(this.locationId)) {
                    object.location_id = this.locationId;
                }
                return object
            })
        },

        apiUrl() {
            const baseUrl = '/api/vehicles';
            const params = new URLSearchParams();

            if (this.hasLocationId) {
                params.append('locationId', this.locationId);
            }

            if (this.hasVehicleGroup) {
                params.append('vehicleGroup', this.vehicleGroup);
            }

            if (this.isBusiness) {
                params.append('isBusiness', true);
            }

            const query = params.toString();

            return query ? `${baseUrl}?${query}` : baseUrl;
        }
    },

    created() {
        if (!lodash.isEmpty(this.predefinedVehicleGroup)) {
            this.vehicleGroup = this.predefinedVehicleGroup;
        }

        this.checkVehicleType();
        this.checkUrlQueryString();
        this.fetchData();
    },

    methods: {
        checkUrlQueryString() {
            let queryString = window.location.search;

            if (lodash.isEmpty(queryString)) {
                return;
            }

            let urlParams = new URLSearchParams(queryString);

            if (urlParams.has('vehicleGroupId')) {
                this.vehicleGroupId = urlParams.get('vehicleGroupId');
            }

            if (urlParams.has('vehicleGroup')) {
                this.vehicleGroup = urlParams.get('vehicleGroup');
            }
        },

        fetchData() {
            axios.get(this.apiUrl)
                .then(response => {
                    this.filterSet = response.data.filters;
                    this.vehicles = response.data.vehicle_wrappers;
                    this.ctas = response.data.ctas;

                    setTimeout(() => this.handleAnalytics(), 2000);
                })
                .catch(error => {
                    // @TODO: mag uit voor prod
                    this.$toaster.error('Er is iets fout gegaan bij het ophalen van de voertuigen');
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        toggleFilterSet() {
            this.filterSetOpen = !this.filterSetOpen;

            this.$emit('toggle-filter-set', this.filterSetOpen)

            if (!this.filterSetOpen) {
                return;
            }


            document.body.addEventListener('click', function (e) {
                let containsActiveClass = false;
                let reviewNode = e.target;

                while (reviewNode.nodeName !== 'BODY') {

                    if (reviewNode.classList.contains('vue-filter-set')) {
                        containsActiveClass = true;
                        break;
                    }

                    reviewNode = reviewNode.parentNode;
                }

                if (containsActiveClass === false && document.body.classList.contains('body-overlay')) {
                    console.log('You clicked! (But not on .active or any of its child elements.)');
                }
            });


            document.body.classList.add('body-overlay');
        },

        handleSort(value) {
            this.sortOption = value;
        },

        handleFiltersChanged(filterSet) {
            this.selectedFilters = filterSet;
        },

        handleFilterSet(value) {
            this.filterSetOpen = value;
        },

        handleAnalytics() {
            window.dataLayer.push({ecommerce: null});
            window.dataLayer.push({
                'event': 'view_item_list',
                'ecommerce': {
                    'item_list_id': this.vehicleGroupId,
                    'currency': 'EUR',
                    'items': this.vehiclesForAnalytics,
                },
            })
        },

        handleViewModeChanged(payload) {
            this.viewMode = payload
        },

        checkVehicleType() {
            if (this.vehicleType === 'bus') {
                this.selectedFilters.push({
                    filters: ['103', '104', '41'],
                    groupId: 'multi_class'
                });

                return;
            }

            if (this.vehicleType === 'car') {
                this.selectedFilters.push({
                    filters: ['102'],
                    groupId: 'multi_class'
                });
            }
        }
    }
}
</script>
